import React from 'react';
import { useTranslation } from 'react-i18next';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReplayIcon from '@mui/icons-material/Replay';

export type HelpSection = 'surveys' | 'recordings' | 'team_of_teams' | 'teams' | 'excel';

const SECTIONS: HelpSection[] = ['surveys', 'recordings', 'team_of_teams', 'teams', 'excel'];

type HelpDrawerProps = {
  open: boolean;
  onClose: () => void;
  onReplayTour: () => void;
  defaultSection?: HelpSection;
};

export default function HelpDrawer({
  open,
  onClose,
  onReplayTour,
  defaultSection,
}: HelpDrawerProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = React.useState<HelpSection | false>(defaultSection ?? false);

  React.useEffect(() => {
    if (open && defaultSection) setExpanded(defaultSection);
  }, [open, defaultSection]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: '100vw', sm: 420 }, p: 2 }} role="presentation">
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="h6">{t('help.drawer_title')}</Typography>
          <IconButton onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {t('help.drawer_intro')}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {SECTIONS.map((section) => (
          <Accordion
            key={section}
            expanded={expanded === section}
            onChange={(_, isExpanded) => setExpanded(isExpanded ? section : false)}
            disableGutters
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">{t(`help.sections.${section}.title`)}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary">
                {t(`help.sections.${section}.body`)}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
        <Box mt={3}>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<ReplayIcon />}
            onClick={() => {
              onClose();
              onReplayTour();
            }}
          >
            {t('help.replay_tour')}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
